import { useId, CSSProperties } from 'react'

interface HotPlateProps {
  temperature?: number // in Celsius
  isOn?: boolean
  setting?: 'low' | 'medium' | 'high'
  hasVessel?: boolean
  onClick?: () => void
  disabled?: boolean
  className?: string
}

const HotPlate = ({
  temperature = 25,
  isOn = false,
  setting = 'medium',
  hasVessel = false,
  onClick,
  disabled = false,
  className = ''
}: HotPlateProps) => {
  const id = useId()

  const width = 160
  const height = 100

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : onClick ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.2s ease'
  }

  // Heat color based on temperature (25-100+ degrees)
  const getHeatColor = () => {
    if (!isOn || temperature < 30) return '#4A4A4A'
    if (temperature < 50) return '#6B4423'
    if (temperature < 70) return '#8B4513'
    if (temperature < 90) return '#CD5C5C'
    return '#FF4500'
  }

  // Glow intensity based on setting
  const getGlowIntensity = () => {
    if (!isOn) return 0
    switch (setting) {
      case 'low': return 0.3
      case 'medium': return 0.5
      case 'high': return 0.8
      default: return 0.5
    }
  }

  const heatColor = getHeatColor()
  const glowIntensity = getGlowIntensity()

  return (
    <svg
      viewBox="0 0 160 100"
      width={width}
      height={height}
      onClick={disabled ? undefined : onClick}
      style={containerStyle}
      className={className}
    >
      <defs>
        {/* Body gradient */}
        <linearGradient id={`hp-body-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#4A4A4A" />
          <stop offset="50%" stopColor="#3A3A3A" />
          <stop offset="100%" stopColor="#2A2A2A" />
        </linearGradient>

        {/* Top surface gradient */}
        <linearGradient id={`hp-surface-${id}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#5A5A5A" />
          <stop offset="100%" stopColor="#3A3A3A" />
        </linearGradient>

        {/* Heat plate gradient */}
        <radialGradient id={`hp-heat-${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={heatColor} />
          <stop offset="70%" stopColor={isOn ? '#3A3A3A' : '#4A4A4A'} />
          <stop offset="100%" stopColor="#3A3A3A" />
        </radialGradient>

        {/* Heat glow */}
        {isOn && (
          <filter id={`hp-glow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feFlood floodColor={heatColor} floodOpacity={glowIntensity} />
            <feComposite in2="blur" operator="in" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}

        {/* Shadow */}
        <filter id={`hp-shadow-${id}`} x="-10%" y="-5%" width="120%" height="120%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35" />
        </filter>
      </defs>

      <g filter={`url(#hp-shadow-${id})`}>
        {/* Main body */}
        <rect
          x="10"
          y="45"
          width="140"
          height="45"
          fill={`url(#hp-body-${id})`}
          rx="5"
        />

        {/* Top surface */}
        <rect
          x="10"
          y="35"
          width="140"
          height="15"
          fill={`url(#hp-surface-${id})`}
          rx="3"
        />

        {/* Heat plate area (ceramic surface) */}
        <rect
          x="25"
          y="8"
          width="110"
          height="32"
          fill="#2A2A2A"
          stroke="#4A4A4A"
          strokeWidth="2"
          rx="3"
        />

        {/* Heat element (coil pattern) */}
        <g filter={isOn ? `url(#hp-glow-${id})` : undefined}>
          <circle
            cx="80"
            cy="24"
            r="28"
            fill="none"
            stroke={heatColor}
            strokeWidth="3"
            opacity={isOn ? 0.8 : 0.3}
          />
          <circle
            cx="80"
            cy="24"
            r="20"
            fill="none"
            stroke={heatColor}
            strokeWidth="2.5"
            opacity={isOn ? 0.7 : 0.25}
          />
          <circle
            cx="80"
            cy="24"
            r="12"
            fill="none"
            stroke={heatColor}
            strokeWidth="2"
            opacity={isOn ? 0.6 : 0.2}
          />
          <circle
            cx="80"
            cy="24"
            r="5"
            fill={heatColor}
            opacity={isOn ? 0.5 : 0.15}
          />
        </g>

        {/* Heat shimmer animation when on */}
        {isOn && temperature > 50 && (
          <g opacity="0.3">
            {[0, 1, 2, 3, 4].map((i) => (
              <line
                key={i}
                x1={50 + i * 15}
                y1={5}
                x2={50 + i * 15}
                y2={-10}
                stroke="rgba(255,100,50,0.5)"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <animate
                  attributeName="y1"
                  values="5;-5;5"
                  dur={`${0.8 + i * 0.1}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="y2"
                  values="-10;-20;-10"
                  dur={`${0.8 + i * 0.1}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.4;0.1;0.4"
                  dur={`${0.8 + i * 0.1}s`}
                  repeatCount="indefinite"
                />
              </line>
            ))}
          </g>
        )}

        {/* Control panel */}
        <rect
          x="20"
          y="55"
          width="120"
          height="28"
          fill="#2A2A2A"
          rx="3"
        />

        {/* Power LED */}
        <circle
          cx="35"
          cy="69"
          r="4"
          fill={isOn ? '#00FF00' : '#333333'}
          stroke="#4A4A4A"
          strokeWidth="1"
        >
          {isOn && (
            <animate
              attributeName="opacity"
              values="1;0.6;1"
              dur="2s"
              repeatCount="indefinite"
            />
          )}
        </circle>
        <text
          x="35"
          y="82"
          fontSize="6"
          fill="#888888"
          textAnchor="middle"
          fontFamily="Arial, sans-serif"
        >
          PWR
        </text>

        {/* Temperature display */}
        <rect
          x="52"
          y="60"
          width="45"
          height="18"
          fill="#001100"
          rx="2"
        />
        <text
          x="74"
          y="73"
          fontSize="12"
          fill="#00FF00"
          textAnchor="middle"
          fontFamily="'Courier New', monospace"
          fontWeight="bold"
        >
          {temperature.toFixed(0)}°C
        </text>

        {/* Setting indicator */}
        <g>
          {(['low', 'medium', 'high'] as const).map((s, i) => (
            <g key={s}>
              <rect
                x={105 + i * 12}
                y="62"
                width="8"
                height="14"
                fill={setting === s && isOn ? '#FF5A36' : '#3A3A3A'}
                stroke="#4A4A4A"
                strokeWidth="0.5"
                rx="1"
              />
              <text
                x={109 + i * 12}
                y="82"
                fontSize="5"
                fill="#888888"
                textAnchor="middle"
                fontFamily="Arial, sans-serif"
              >
                {s.charAt(0).toUpperCase()}
              </text>
            </g>
          ))}
        </g>

        {/* Feet */}
        <rect x="20" y="88" width="15" height="5" fill="#2A2A2A" rx="2" />
        <rect x="125" y="88" width="15" height="5" fill="#2A2A2A" rx="2" />

        {/* Brand label */}
        <rect
          x="60"
          y="47"
          width="40"
          height="6"
          fill="#FF5A36"
          rx="1"
        />
        <text
          x="80"
          y="52"
          fontSize="5"
          fill="white"
          textAnchor="middle"
          fontWeight="bold"
          fontFamily="Arial, sans-serif"
        >
          HOT PLATE
        </text>

        {/* Vessel placeholder indicator */}
        {hasVessel && (
          <circle
            cx="80"
            cy="24"
            r="30"
            fill="none"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="1"
            strokeDasharray="4,4"
          />
        )}
      </g>

      {/* Label */}
      <text
        x="80"
        y="98"
        fontSize="9"
        fill="var(--text-primary, #FFFFFF)"
        textAnchor="middle"
        fontWeight="500"
        fontFamily="Arial, sans-serif"
      >
        Hot Plate
      </text>
    </svg>
  )
}

export default HotPlate
